import * as React from 'react'

import { Box, Button, type Icon } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import i18n from 'modules/core/lib/i18n'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import { InputItem } from 'modules/custom_resources/components/CustomFields/CustomFieldsForm/InputItem'
import resources from 'modules/custom_resources/store/collections/customResources/resources'
import type Field from 'modules/custom_resources/store/models/customResources/Field'
import type { IResource } from 'modules/custom_resources/store/models/customResources/Resource'
import type Resource from 'modules/custom_resources/store/models/customResources/Resource'
import type Schema from 'modules/custom_resources/store/models/customResources/Schema'
import type Value from 'modules/custom_resources/store/models/customResources/Value'
import {
  createForm,
  submitForms,
} from 'modules/employees/components/CustomResources/SchemaComponent/Form/utils'
import { RouteContext } from 'modules/employees/pages/CustomTablesListPage/context'
import employees from 'modules/employees/store/collections/employees'

interface Props {
  illustration?: React.ComponentProps<typeof Icon.Large>['icon']
  title?: string
  description?: string
  schema: Schema | undefined
  fields: Field[]
  resource?: Resource | null | undefined
  attachableId: number
  attachableType: string
  value?: Value
  onClose: () => void
}

const FormModal = ({
  illustration = 'table-file',
  title = i18n.t('custom_tables.modal.title'),
  description = i18n.t('custom_tables.modal.description'),
  schema,
  fields,
  resource,
  attachableId,
  attachableType,
  value,
  onClose,
}: Props) => {
  const rootRoute = React.useContext(RouteContext)
  const employee = employees.get(+attachableId)
  const canRemoveValues = employee?.policy.can('removeCustomTablesRecords')

  const forms = React.useRef(
    fields.map((field) =>
      createForm({
        schema,
        field,
        resource,
        currentValue: value,
        employee,
        contractVersions,
      })
    )
  ).current

  const createResource = async (): Promise<IResource> => {
    return resources.create({
      schema_id: schema?.attributes.id,
      attachable_id: attachableId,
      attachable_type: attachableType,
    })
  }

  const upsertValue = (resource?: Resource) => {
    return submitForms({
      resource,
      fields,
      forms,
      currentValue: value,
    })
  }

  const { performAction } = useAsyncAction(async (closePortal: ClosePortalFn) => {
    let resourceModel = resource

    if (!resourceModel) {
      let value
      try {
        value = await createResource()
      } finally {
        resourceModel = value && resources.get(value.id)
      }
    }

    await upsertValue(resourceModel)

    closePortal()
  })

  const submitEnabled = forms.some((f) => f.isDirty)

  return (
    <Modal
      illustration={illustration}
      title={title}
      description={description}
      onSubmit={performAction}
      onClose={onClose}
      footer={
        <Box flexDirection="row" justifyContent="flexEnd" gap="s8" background="grey100">
          {!!value && canRemoveValues && rootRoute && schema && (
            <Button.Router
              to={rootRoute.customTablesDelete(schema.attributes.id, value.attributes.id)}
              type="negative"
            >
              {i18n.t('custom_tables.modal.delete')}
            </Button.Router>
          )}

          <Button disabled={!submitEnabled} submit>
            {i18n.t('custom_tables.modal.submit')}
          </Button>
        </Box>
      }
    >
      {forms.map((form: Form, key: number) => (
        <FormRow key={key}>
          <InputItem form={form} currency={employee?.attributes.legal_entity_currency} />
        </FormRow>
      ))}
    </Modal>
  )
}

export default observer(FormModal)
